import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import UserIdentitiesService, { UserIdentityFlowAction } from "@/services/UserIdentitiesService";
import type { UserIdentityStatusDto } from "@/services/UserIdentitiesService";
import CompanyOnboardingService from "@/services/CompanyOnboardingService";
import type { LegalRepresentativeResultDto } from "@/services/CompanyOnboardingService";
import { mapAnalyzeDocumentResponseToProcessingResult } from "@/screen/Identities/identityDocumentAnalysisMapper";
import { IdentitiesPath } from "@/screen/Identities/identitiesRoutes";
import { CompanySubscriptionPath } from "@/screen/Companies/companiesRoutes";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { toCompanyOnboardingErrorMessages } from "@/screen/Companies/companyOnboardingErrors";
import { applyIdDocumentReading, buildCreateLegalRepresentativeRequest, getInitialRepresentativeFormData, getMissingRepresentativeFieldLabels, isRepresentativeFormComplete } from "@/screen/Companies/CompanyRepresentative/companyRepresentativeForm";
import type { RepresentativeFormData } from "@/screen/Companies/CompanyRepresentative/companyRepresentativeForm";
import { getAccessRequestTargetId, getExistingRepresentativeKind } from "@/screen/Companies/CompanyRepresentative/existingRepresentativeRules";

//  upload    → subiendo anverso y reverso
//  reading   → analyzeDocument + analyzeStatus en curso
//  review    → datos leídos (o vacíos si no se pudieron leer) y datos adicionales
//  existing  → la persona ya existe en IronIA y no se puede dar de alta otra vez
//  done      → representante dado de alta
export type RepresentativePhase = "upload" | "reading" | "review" | "existing" | "done";

// Con estas acciones se puede seguir con el alta: persona nueva o alta propia a medias (el backend la reutiliza)
const ActionsAllowingSignUp: (UserIdentityFlowAction | null)[] = [null, UserIdentityFlowAction.StartNewOnboarding, UserIdentityFlowAction.ResumeDraft];

export function useCompanyRepresentativeController()
{
    const history = useHistory();
    const [idFront, setIdFront] = useState<File | null>(null);
    const [idBack, setIdBack] = useState<File | null>(null);
    const [phase, setPhase] = useState<RepresentativePhase>("upload");
    const [formData, setFormData] = useState<RepresentativeFormData>(getInitialRepresentativeFormData);
    const [readingWarning, setReadingWarning] = useState<string | null>(null);
    const [existingStatus, setExistingStatus] = useState<UserIdentityStatusDto | null>(null);
    const [isRequestingAccess, setIsRequestingAccess] = useState<boolean>(false);
    const [accessRequested, setAccessRequested] = useState<boolean>(false);
    const [accessRequestErrors, setAccessRequestErrors] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitErrors, setSubmitErrors] = useState<string[]>([]);
    const [result, setResult] = useState<LegalRepresentativeResultDto | null>(null);

    // Cambiar una imagen obliga a volver a leer el DNI
    const handleIdFrontChange = useCallback((file: File) =>
    {
        setIdFront(file);
        setPhase((prev) => (prev === "done" ? prev : "upload"));
    }, []);

    const handleIdBackChange = useCallback((file: File) =>
    {
        setIdBack(file);
        setPhase((prev) => (prev === "done" ? prev : "upload"));
    }, []);

    const handleReadIdDocument = useCallback(async () =>
    {
        if(!idFront || !idBack)
        {
            return;
        }

        setPhase("reading");
        setSubmitErrors([]);
        setExistingStatus(null);
        setAccessRequested(false);
        setAccessRequestErrors([]);

        const analysisForm = new FormData();
        analysisForm.append("idFront", idFront);
        analysisForm.append("idBack", idBack);
        const analysis = await UserIdentitiesService.analyzeDocument(analysisForm);
        const mapped = mapAnalyzeDocumentResponseToProcessingResult(analysis, "", "");
        const reading = mapped.ok ? mapped.result : mapped.partialResult;
        const nextData = applyIdDocumentReading(formData, reading);
        setFormData(nextData);
        setReadingWarning(mapped.ok ? null : CompanyOnboardingStrings.idReadingIncomplete);

        // Si la persona ya existe en IronIA no se vuelve a dar de alta
        if(nextData.idDocument)
        {
            const status = await UserIdentitiesService.analyzeIdentityStatus(nextData.idDocument);
            const statusData = status.success ? (status.data as UserIdentityStatusDto | null) : null;
            if(statusData && !ActionsAllowingSignUp.includes(statusData.action))
            {
                setExistingStatus(statusData);
                setPhase("existing");
                return;
            }
        }

        setPhase("review");
    }, [idFront, idBack, formData]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
        setSubmitErrors([]);
    }, []);

    const handlePermanentIdExpirationChange = useCallback((checked: boolean) =>
    {
        setFormData((prev) => ({...prev, hasPermanentIdExpiration: checked}));
    }, []);

    const handleSubmit = useCallback(async () =>
    {
        if(!idFront || !idBack || !isRepresentativeFormComplete(formData))
        {
            return;
        }

        setIsSubmitting(true);
        setSubmitErrors([]);
        const response = await CompanyOnboardingService.createLegalRepresentative(buildCreateLegalRepresentativeRequest(formData), idFront, idBack);
        setIsSubmitting(false);

        if(response.success && response.data)
        {
            setResult(response.data as LegalRepresentativeResultDto);
            setPhase("done");
            return;
        }

        setSubmitErrors(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.submitGenericError));
    }, [idFront, idBack, formData]);

    // Interviniente de otro usuario: préstamo con aprobación del titular. Cuando lo apruebe aparecerá en Gestión de
    // Intervinientes y se podrá seleccionar como representante en el alta de la empresa.
    const handleRequestAccess = useCallback(async () =>
    {
        const targetId = getAccessRequestTargetId(existingStatus);
        if(!targetId)
        {
            setAccessRequestErrors([CompanyOnboardingStrings.accessRequestGenericError]);
            return;
        }

        setIsRequestingAccess(true);
        setAccessRequestErrors([]);
        const response = await UserIdentitiesService.requestUserIdentityAccess(targetId);
        setIsRequestingAccess(false);

        if(response.success)
        {
            setAccessRequested(true);
            return;
        }

        setAccessRequestErrors(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.accessRequestGenericError));
    }, [existingStatus]);

    const goToIdentities = useCallback(() =>
    {
        history.push(IdentitiesPath);
    }, [history]);

    const goToCompanySubscription = useCallback(() =>
    {
        history.push(CompanySubscriptionPath);
    }, [history]);

    // Una empresa suele tener varios representantes: se vuelve a la pantalla vacía para dar de alta otro
    const handleAddAnother = useCallback(() =>
    {
        setIdFront(null);
        setIdBack(null);
        setFormData(getInitialRepresentativeFormData());
        setReadingWarning(null);
        setExistingStatus(null);
        setAccessRequested(false);
        setAccessRequestErrors([]);
        setSubmitErrors([]);
        setResult(null);
        setPhase("upload");
        window.scrollTo(0, 0);
    }, []);

    return{
        idFront,
        idBack,
        phase,
        formData,
        readingWarning,
        existingStatus,
        isSubmitting,
        submitErrors,
        result,
        existingKind: getExistingRepresentativeKind(existingStatus),
        isRequestingAccess,
        accessRequested,
        accessRequestErrors,
        canReadIdDocument: !!idFront && !!idBack && phase === "upload",
        canSubmit: !isSubmitting && isRepresentativeFormComplete(formData),
        // Lo que falta para poder dar de alta: se enseña mientras el botón está deshabilitado
        missingFieldLabels: getMissingRepresentativeFieldLabels(formData),
        handleIdFrontChange,
        handleIdBackChange,
        handleReadIdDocument,
        handleChange,
        handlePermanentIdExpirationChange,
        handleSubmit,
        handleRequestAccess,
        handleAddAnother,
        goToIdentities,
        goToCompanySubscription,
    };
}
